import { useEffect, useState } from "react";
import _ from "lodash";

interface DataFilter {
  filter: {
    sortBy: {
      column: {
        key: string;
        sortKey?: string;
        disableSortBy?: boolean;
        sortType?: string;
      };
      desc: boolean;
    };
    pageSize: number;
    pageIndex: number;
  };
  setPageCount: (count: number) => void;
  setPaging: (pageIndex: number, pageSize: number) => void;
  setTotalItems: (items: { countByFilter: number }) => void;
  getPageData: () => { start: number; limit: number };
}

export const useClientSideData = (
  dataFilter: DataFilter, 
  allData: unknown[], 
  search?: string, 
  searchKeys?: string[]
) => {
  const [data, setData] = useState<unknown[]>([]);

  const {
    filter,
    setPageCount,
    setPaging,
    setTotalItems,
    getPageData,
  } = dataFilter;

  useEffect(() => {
    let data = [...allData];
    if (!filter) return;

    if (filter.sortBy.column.key && !filter.sortBy.column.disableSortBy) {
      const sortKey = filter.sortBy.column.sortKey || filter.sortBy.column.key;
      data = _.orderBy(
        data,
        [
          (item: Record<string, unknown>) => {
            const sortValue = _.get(item, sortKey) as unknown;
            if (filter.sortBy.column.sortType) {
              // todo: add more cases
              switch (filter.sortBy.column.sortType) {
                case "fwVersion": {
                  const major = Number(item.fwMajor ?? 0);
                  const minor = Number(item.fwMinor ?? 0);

                  return major * 1000 + minor;
                }
                case "tag":
                default:
                  return (
                    sortValue &&
                    Array.isArray(sortValue) &&
                    sortValue.map((item: any) => item && item.name).join(" ") // eslint-disable-line @typescript-eslint/no-explicit-any
                  );
              }
            }
            if (typeof sortValue === "string") {
              return sortValue.toLowerCase();
            } else {
              return sortValue;
            }
          },
        ],
        filter.sortBy.desc ? ["desc"] : ["asc"]
      );
    }
    if (search && searchKeys && searchKeys.length) {
      data = data.filter((row) => {
        for (let i = 0; i < searchKeys.length; i++) {
          const searchKey = searchKeys[i];
          const rowObj = row as Record<string, unknown>;
          if (
            rowObj[searchKey] &&
            rowObj[searchKey]
              .toString()
              .toLowerCase()
              .indexOf(search.toLowerCase()) !== -1
          ) {
            return true; // Early exit if a match is found
          }
        }
        return false; // No match found
      });
    }
    const { start, limit } = getPageData();
    setData(data.slice(start, start + limit));
    const pageNrOf = Math.ceil(data.length / filter.pageSize);
    setPageCount(pageNrOf);
    // Only clamp once there is data: on first render the parent hasn't
    // loaded yet (allData = []), and pageNrOf 0 would wipe a restored
    // pageIndex before the real row count is known (STIC-15588).
    if (pageNrOf > 0 && filter.pageIndex >= pageNrOf) {
      setPaging(0, filter.pageSize);
    }
    setTotalItems({ countByFilter: data.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, allData, filter]);

  return {
    data,
  };
};
